import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import type { Page } from './types';

interface ApiQuery<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Small data-fetching hook: GET on mount + manual reload. Pass null to skip. */
export function useApiQuery<T>(path: string | null): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((d) => {
        if (alive.current) setData(d);
      })
      .catch((e: unknown) => {
        if (alive.current) setError(e instanceof ApiError ? e.message : 'Request failed');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

interface CursorList<T> {
  items: T[];
  nextCursor: string | null;
  loading: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

/**
 * Cursor-paginated list hook shared by every staff list screen across the
 * three module workspaces (M17/M19/M20 list endpoints all return the same
 * `{ items, nextCursor }` envelope — see src/lib/types.ts's `Page<T>`).
 * `basePath` should already carry every filter query param except `cursor`;
 * changing it (e.g. a status filter) triggers a fresh fetch-from-scratch.
 * Pass `null` to skip fetching (e.g. while a required filter is unset).
 */
export function useCursorList<T>(basePath: string | null): CursorList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const fetchPage = useCallback(async (path: string, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.get<Page<T>>(path);
      if (!alive.current) return;
      setItems((prev) => (append ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
    } catch (e) {
      if (alive.current) setError(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'Request failed');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (basePath === null) {
      setItems([]);
      setNextCursor(null);
      return;
    }
    fetchPage(basePath, false);
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  const loadMore = useCallback(() => {
    if (!nextCursor || basePath === null) return;
    const sep = basePath.includes('?') ? '&' : '?';
    fetchPage(`${basePath}${sep}cursor=${encodeURIComponent(nextCursor)}`, true);
  }, [basePath, nextCursor, fetchPage]);

  const reload = useCallback(() => {
    if (basePath !== null) fetchPage(basePath, false);
  }, [basePath, fetchPage]);

  return { items, nextCursor, loading, error, loadMore, reload };
}
